import React, { useState, useEffect } from 'react';
import './BannedPage.css';

function BannedPage({ message, bannedUntil, onRetry }) {
    const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
    const [isExpired, setIsExpired] = useState(false);

    useEffect(() => {
        if (!bannedUntil) return;

        const updateCountdown = () => {
            const now = new Date();
            const end = new Date(bannedUntil);
            const diff = end - now;

            if (diff <= 0) {
                setIsExpired(true);
                return;
            }

            const days = Math.floor(diff / (1000 * 60 * 60 * 24));
            const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((diff % (1000 * 60)) / 1000);

            setCountdown({ days, hours, minutes, seconds });
        };

        updateCountdown();
        const interval = setInterval(updateCountdown, 1000);

        return () => clearInterval(interval);
    }, [bannedUntil]);

    const handleLogout = () => {
        localStorage.removeItem('token');
        window.location.href = '/login';
    };

    return (
        <div className="banned-container">
            <div className="banned-bg">
                <div className="banned-circle circle-1"></div>
                <div className="banned-circle circle-2"></div>
                <div className="banned-circle circle-3"></div>
            </div>

            <div className="banned-content">
                <div className="banned-icon">
                    <svg viewBox="0 0 24 24" width="80" height="80" fill="currentColor">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zM4 12c0-4.42 3.58-8 8-8 1.85 0 3.55.63 4.9 1.69L5.69 16.9C4.63 15.55 4 13.85 4 12zm8 8c-1.85 0-3.55-.63-4.9-1.69L18.31 7.1C19.37 8.45 20 10.15 20 12c0 4.42-3.58 8-8 8z"/>
                    </svg>
                </div>

                <h1>บัญชีถูกระงับ</h1>
                <h2>Account Suspended</h2>

                <div className="banned-message">
                    {message || 'บัญชีของคุณถูกระงับการใช้งาน กรุณาติดต่อผู้ดูแลระบบ'}
                </div>

                {bannedUntil && !isExpired && (
                    <div className="ban-duration">
                        <p>การระงับจะสิ้นสุดในอีก:</p>
                        <div className="countdown">
                            {countdown.days > 0 && (
                                <>
                                    <div className="countdown-item">
                                        <span className="number">{String(countdown.days).padStart(2, '0')}</span>
                                        <span className="label">วัน</span>
                                    </div>
                                    <span className="separator">:</span>
                                </>
                            )}
                            <div className="countdown-item">
                                <span className="number">{String(countdown.hours).padStart(2, '0')}</span>
                                <span className="label">ชั่วโมง</span>
                            </div>
                            <span className="separator">:</span>
                            <div className="countdown-item">
                                <span className="number">{String(countdown.minutes).padStart(2, '0')}</span>
                                <span className="label">นาที</span>
                            </div>
                            <span className="separator">:</span>
                            <div className="countdown-item">
                                <span className="number">{String(countdown.seconds).padStart(2, '0')}</span>
                                <span className="label">วินาที</span>
                            </div>
                        </div>
                        <p className="ban-end-date">
                            สิ้นสุดวันที่: {new Date(bannedUntil).toLocaleString('th-TH', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                            })}
                        </p>
                    </div>
                )}

                {!bannedUntil && (
                    <div className="permanent-ban">
                        <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                        </svg>
                        <span>การระงับนี้เป็นแบบถาวร</span>
                    </div>
                )}

                {isExpired && (
                    <div className="ban-expired">
                        <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                        </svg>
                        <span>การระงับสิ้นสุดแล้ว คุณสามารถเข้าสู่ระบบได้อีกครั้ง</span>
                    </div>
                )}

                <div className="banned-footer">
                    <p>หากคุณเชื่อว่านี่คือความผิดพลาด กรุณาติดต่อผู้ดูแลระบบ</p>
                    <div className="banned-actions">
                        {isExpired && onRetry && (
                            <button className="retry-btn" onClick={onRetry}>
                                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                                    <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
                                </svg>
                                ลองใหม่
                            </button>
                        )}
                        <button className="logout-btn" onClick={handleLogout}>
                            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                                <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"/>
                            </svg>
                            ออกจากระบบ
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default BannedPage;
